/**
 * Ролевые пресеты навигации админки.
 *
 * Пресеты фиксированные (кассир, старший смены, директор): набор разделов
 * по умолчанию известен заранее. Директор может поменять только маппинг
 * «роль → разделы»; сам список ролей не редактируется.
 *
 * Конфиг хранится в облаке (`GET/PUT /sync/role-presets`) и кэшируется
 * локально в `meta`, чтобы админка открывалась офлайн с последней
 * известной раскладкой.
 */

import type { ActiveCashier } from '@core/components/PinPadModal'
import type { AdminSectionId } from '@core/pages/admin/types'

export type RolePresetId = 'cashier' | 'senior' | 'director'

export interface RolePresetInfo {
  id: RolePresetId
  label: string
  /** Разделы по умолчанию; `all` — все доступные правами. */
  sections: AdminSectionId[] | 'all'
  defaultSection: AdminSectionId
}

/** Порядок важен для UI редактора. */
export const ROLE_PRESETS: RolePresetInfo[] = [
  {
    id: 'cashier',
    label: 'Кассир',
    // В админку кассир без can_open_admin не попадает; если попал — только обзор.
    sections: ['overview'],
    defaultSection: 'overview'
  },
  {
    id: 'senior',
    label: 'Старший смены',
    sections: ['overview', 'monitoring', 'sales', 'reports', 'equipment', 'documents'],
    // Старшему смены чаще нужны продажи, а не обзор.
    defaultSection: 'sales'
  },
  {
    id: 'director',
    label: 'Директор',
    sections: 'all',
    defaultSection: 'overview'
  }
]

export interface RoleNavigationConfig {
  /** Версия схемы конфига (для миграций). */
  version: number
  /** Когда конфиг изменён (ms) — для last-write-wins между облаком и локалью. */
  updatedAt: number
  /** Переопределения наборов; отсутствие ключа = пресет по умолчанию. */
  byRole: Partial<Record<RolePresetId, AdminSectionId[]>>
}

export const ROLE_NAVIGATION_VERSION = 1

export const DEFAULT_ROLE_NAVIGATION: RoleNavigationConfig = {
  version: ROLE_NAVIGATION_VERSION,
  updatedAt: 0,
  byRole: {}
}

const ALL_SECTION_IDS: AdminSectionId[] = [
  'overview',
  'sales',
  'reports',
  'catalog',
  'equipment',
  'documents',
  'staff',
  'organization',
  'integrations',
  'monitoring',
  'roles'
]

/** Определить пресет по активному сотруднику. */
export function resolveRolePreset(
  cashier?: Pick<ActiveCashier, 'role' | 'permissions'> | null
): RolePresetId {
  if (!cashier) return 'director'
  if (cashier.role === 'admin') return 'director'
  if (cashier.permissions?.can_open_admin === true) return 'senior'
  return 'cashier'
}

/** Разделы роли: `all` — все, иначе переопределение или пресет. */
export function sectionsForRole(
  role: RolePresetId,
  config: RoleNavigationConfig = DEFAULT_ROLE_NAVIGATION
): AdminSectionId[] | 'all' {
  const override = config.byRole[role]
  if (override) return override
  return ROLE_PRESETS.find((p) => p.id === role)?.sections ?? 'all'
}

export function defaultSectionForRole(role: RolePresetId): AdminSectionId {
  return ROLE_PRESETS.find((p) => p.id === role)?.defaultSection ?? 'overview'
}

/** Оставить только известные id, без дубликатов, в каноническом порядке. */
export function sanitizeSections(value: unknown): AdminSectionId[] {
  const list = Array.isArray(value) ? value : []
  const seen = new Set<AdminSectionId>()
  for (const item of list) {
    if (typeof item !== 'string') continue
    if (!ALL_SECTION_IDS.includes(item as AdminSectionId)) continue
    seen.add(item as AdminSectionId)
  }
  return ALL_SECTION_IDS.filter((id) => seen.has(id))
}

/** Разбор конфига из облака/локального кэша. Мусор превращается в дефолт. */
export function normalizeRoleNavigation(raw: unknown): RoleNavigationConfig {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ROLE_NAVIGATION, byRole: {} }
  const source = raw as {
    version?: unknown
    updatedAt?: unknown
    byRole?: unknown
  }
  const byRole: Partial<Record<RolePresetId, AdminSectionId[]>> = {}
  if (source.byRole && typeof source.byRole === 'object') {
    for (const role of ROLE_PRESETS) {
      const value = (source.byRole as Record<string, unknown>)[role.id]
      if (Array.isArray(value)) byRole[role.id] = sanitizeSections(value)
    }
  }
  return {
    version: typeof source.version === 'number' ? source.version : ROLE_NAVIGATION_VERSION,
    updatedAt: typeof source.updatedAt === 'number' ? source.updatedAt : 0,
    byRole
  }
}

/** Конфликт облака и локального кэша: побеждает более поздний. */
export function pickLatestRoleNavigation(
  remote: unknown,
  local: unknown
): RoleNavigationConfig {
  const a = normalizeRoleNavigation(remote)
  const b = normalizeRoleNavigation(local)
  return a.updatedAt >= b.updatedAt ? a : b
}

/**
 * Итоговый набор разделов: пересечение пресета роли и прав,
 * порядок — как в реестре `ADMIN_SECTIONS`.
 */
export function applyRoleNavigation<T extends { id: AdminSectionId }>(
  entries: T[],
  role: RolePresetId,
  config: RoleNavigationConfig
): T[] {
  const allowed = sectionsForRole(role, config)
  if (allowed === 'all') return entries
  const set = new Set(allowed)
  return entries.filter((entry) => set.has(entry.id))
}
